import { Div, Span, Button, Input, router, state, IRouteEvent } from '@codesuma/baseline'
import { SheetPage } from '../../components/sheet-page'
import search, { ISearchRow, ResultKind } from '../../services/search'
import vectors from '../../services/search-vectors'
import settings, { CalendarSystem } from '../../services/settings'
import { isPrimary } from '../../services/day-holidays'
import { dayLine } from '../../helpers/day-line'
import { paleYear } from '../../helpers/year'
import { toFaDigits } from '../../helpers/digits'
import style from './index.module.css'

// The search page (route /search): one field, and under it everything that
// answers what was typed — occasions and holidays, the reader's own occasions,
// events, tasks and notes — each row saying what it is and when it is. A tap
// opens the day it is on, on the tab that lists it. What counts as an answer
// is services/search.ts.
//
// The results follow the typing; the data is in memory, so there is nothing
// to wait for and nothing to submit. Opened from home the field is empty and
// focused; coming back from a result it still holds the query and the rows,
// so a second result is one tap away.

// What the field offers before anything is typed. Tapping one runs it.
const EXAMPLES = [
    'تولد مامان',
    'سررسید چک',
    'تعطیلات امسال',
    'کارهای فردا',
    'سالگرد ازدواج',
    'عید فطر',
    'روز پدر',
    'روز برنامه‌نویس',
    'نوبت دکتر',
    'قرارهای این هفته',
    'تعطیلات محرم',
]

const KIND: Record<ResultKind, string> = {
    occasion: 'رویداد',
    holiday: 'تعطیل رسمی',
    country: '', // the country's own name stands here
    mine: 'رویداد شما',
    event: 'رویداد شما',
    task: 'کار',
    note: 'یادداشت',
}

// How far the row's day is from today, in words a glance can take in.
const relative = (days: number) => {
    if (days === 0) return 'امروز'
    if (days === 1) return 'فردا'
    if (days === 2) return 'پس‌فردا'
    if (days === -1) return 'دیروز'
    if (days === -2) return 'پریروز'
    return days > 0 ? `${toFaDigits(days)} روز دیگر` : `${toFaDigits(-days)} روز پیش`
}

// Every store a row can come from, and the two things that move a row's day.
const WATCHED = ['tasks', 'events', 'notes', 'custom-occasions', 'occasion-overrides',
    'country-occasions', 'hijri', 'settings', 'today']

export const SearchPage = () => {
    const base = SheetPage()

    // No title: the field stands where the title would, beside the chevron,
    // so the first thing on the page is the place to type.
    const field = Input('رویداد، کار یا یادداشت…', 'search', { autocomplete: 'off' })
    field.addClass(style.field)
    field.attr('dir', 'auto')
    field.attr('enterkeyhint', 'search')
    field.attr('aria-label', 'جستجو')
    base.heading.empty()
    base.heading.append(field)

    const list = Div()
    list.addClass(style.list)
    base.append(list)

    const row = (r: ISearchRow, system: CalendarSystem) => {
        const wrap = Div()
        wrap.addClass(style.row)
        wrap.attr('role', 'button')
        wrap.attr('tabindex', '0')
        wrap.el.onclick = () => router.goto(r.route)
        wrap.el.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                wrap.el.click()
            }
        })

        const title = Div(r.title)
        title.addClass(style.title)
        title.attr('dir', 'auto')
        // The holiday colour follows the calendar being read, the same way the
        // grid and the day page do — see services/day-holidays.ts.
        if (r.isHoliday) title.addClass(isPrimary(r.source, system) ? style.holiday : style.holidayOther)
        if (r.done) title.addClass(style.done)
        wrap.append(title)

        // What it is and when it is on one side, how far off that is on the
        // other — so the distance wraps as a whole rather than mid-phrase.
        const kind = r.kind === 'country' ? r.country! : r.done ? 'کار انجام‌شده' : KIND[r.kind]
        const sub = Div()
        sub.addClass(style.sub)
        const what = Span()
        const date = Span()
        // html rather than text: the line carries the pale-year markup.
        date.html(paleYear(dayLine(r.day, system)))
        what.append(Span(r.time ? `${kind}، ${r.time}` : kind), Span(' · '), date)
        sub.append(what, Span(relative(r.distance)))
        wrap.append(sub)

        return wrap
    }

    const examples = () => {
        const hint = Div('همه‌جا بگردید: رویدادها و تعطیلات، کارها و یادداشت‌ها. مثلاً:')
        hint.addClass(style.hint)
        // list.append(hint)

        const chips = Div()
        chips.addClass(style.chips)
        for (const text of EXAMPLES) {
            const chip = Button(text)
            chip.addClass(style.chip)
            chip.on('click', () => {
                field.setValue(text)
                run()
            })
            chips.append(chip)
        }
        list.append(chips)
    }

    const run = () => {
        list.empty()
        const text = field.value().trim()
        if (!text) return examples()

        const rows = search.search(text)
        if (!rows.length) {
            const empty = Div('چیزی پیدا نشد.')
            empty.addClass(style.empty)
            list.append(empty)
            return
        }
        const system = settings.get().system
        for (const r of rows) list.append(row(r, system))
    }

    field.on('input', run)
    // The results are already there, so the return key only puts the keyboard
    // away and gives them the screen.
    field.on('enter', () => field.blur())

    // Opened from home: a fresh field, ready to type into. Coming back from a
    // result keeps the query, the rows, and the keyboard out of the way.
    //
    // Either way the word vectors are asked for. The first time they arrive
    // the rows are drawn again with them; after that this costs nothing.
    base.on('enter', ({ isBack }: IRouteEvent) => {
        void vectors.load().then(run)
        if (isBack) return
        field.clear()
        run()
        // After the page has settled, so the keyboard does not open onto a
        // moving field.
        setTimeout(() => field.focus(), 220)
    })

    const refresh = () => {
        search.invalidate()
        run()
    }
    for (const key of WATCHED) state.on(key, refresh)
    base.on('unmounted', () => {
        for (const key of WATCHED) state.off(key, refresh)
    })

    run()
    return base
}
